import {
  ReactNode,
  createContext,
  useState,
  useMemo,
  useCallback,
} from 'react';
import {
  ConfirmationModalState,
  ConfirmationModalValues,
} from '@app/types/core/confirmation-modal';
import ConfirmationModal from '@components/ConfirmationModal';
import { IconAlertTriangle, IconTrash } from '@tabler/icons-react';

const ConfirmationModalContext = createContext<ConfirmationModalValues>(
  {} as ConfirmationModalValues
);

interface ConfirmationModalProviderProps {
  children: ReactNode;
}

function ConfirmationModalProvider({
  children,
}: ConfirmationModalProviderProps) {
  const [modalState, setModalState] = useState<ConfirmationModalState | null>(
    null
  );

  const closeModal = useCallback(() => {
    setModalState(null);
  }, []);

  const showConfirmationModal = useCallback((state: ConfirmationModalState) => {
    setModalState({
      ...state,
      onConfirm: () => {
        state.onConfirm();
      },
    });
  }, []);

  const showDeleteConfirmation = useCallback(
    (onConfirm: () => void, content?: string) => {
      showConfirmationModal({
        title: 'Delete item',
        content:
          content ||
          'Are you sure you want to delete this item? This action cannot be undone.',
        onConfirm,
        icon: <IconTrash />,
        color: 'error',
        confirmLabel: 'Yes, delete',
      });
    },
    [showConfirmationModal]
  );

  const showAlertConfirmation = useCallback(
    (title: string, content: string, onConfirm: () => void) => {
      showConfirmationModal({
        title,
        content,
        onConfirm,
        icon: <IconAlertTriangle />,
        color: 'primary',
        confirmLabel: 'Confirm',
      });
    },
    [showConfirmationModal]
  );

  const value = useMemo(
    () => ({
      showConfirmationModal,
      showDeleteConfirmation,
      showAlertConfirmation,
      closeModal,
    }),
    [
      showConfirmationModal,
      showDeleteConfirmation,
      showAlertConfirmation,
      closeModal,
    ]
  );

  return (
    <ConfirmationModalContext.Provider value={value}>
      {children}
      {modalState && (
        <ConfirmationModal
          showModal
          onClose={modalState.onClose || closeModal}
          onConfirm={modalState.onConfirm}
          icon={modalState.icon}
          title={modalState.title}
          content={modalState.content}
          cancelLabel={modalState.cancelLabel}
          confirmLabel={modalState.confirmLabel}
          color={modalState.color}
        />
      )}
    </ConfirmationModalContext.Provider>
  );
}

export { ConfirmationModalContext, ConfirmationModalProvider };
